/* eslint-disable */
// @ts-nocheck
/*
 *
 *   AuthProvider.tsx
 *
 *
 *  Created by Christopher Fahlin on 11/17/2021
 *
 *  dha-login
 *
 *  Copyright © 2009-2023 United States Government as represented by
 *  the Program Manager of the DHA: Web and Mobile Technology Program Management Office
 *  All Rights Reserved.
 *
 *  Copyright © 2009-2023 Contributors. All Rights Reserved.
 *
 *  THIS OPEN SOURCE AGREEMENT ("AGREEMENT") DEFINES THE RIGHTS OF USE,
 *  REPRODUCTION, DISTRIBUTION, MODIFICATION AND REDISTRIBUTION OF CERTAIN
 *  COMPUTER SOFTWARE ORIGINALLY RELEASED BY THE UNITED STATES GOVERNMENT
 *  AS REPRESENTED BY THE GOVERNMENT AGENCY LISTED BELOW ("GOVERNMENT AGENCY").
 *  THE UNITED STATES GOVERNMENT, AS REPRESENTED BY GOVERNMENT AGENCY, IS AN
 *  INTENDED THIRD-PARTY BENEFICIARY OF ALL SUBSEQUENT DISTRIBUTIONS OR
 *  REDISTRIBUTIONS OF THE SUBJECT SOFTWARE. ANYONE WHO USES, REPRODUCES,
 *  DISTRIBUTES, MODIFIES OR REDISTRIBUTES THE SUBJECT SOFTWARE, AS DEFINED
 *  HEREIN, OR ANY PART THEREOF, IS, BY THAT ACTION, ACCEPTING IN FULL THE
 *  RESPONSIBILITIES AND OBLIGATIONS CONTAINED IN THIS AGREEMENT.
 *
 *  Government Agency: DHA: Web and Mobile Technology Program Management Office
 *  Government Agency Original Software Designation: dha-login
 *  Government Agency Original Software Title: dha-login
 *  User Registration Requested. Please send email
 *  with your contact information to: robert.a.kayl.civ@mail.mil
 *  Government Agency Point of Contact for Original Software - Program Manager: robert.a.kayl.civ@mail.mil
 *
 */

import React, { useContext, useState, useEffect, useMemo, useRef } from 'react';

import ReactLoading from 'react-loading';

import { decrypt } from 'dha-encryption';
import { LoginDatabaseContext } from './LoginDatabaseProvider';
import useSessionStorageEncoded from '../hooks/useSessionStorageEncoded';

export type IAuthContext = {
  authenticated: boolean;
  username: string;
  setAuthenticated: (isAuthenticated: boolean) => void;
  setUsername: (username: string) => void;
};

const noop = () => {};
/**
 * Authenticated context
 *
 * @remarks
 * authenticated is a boolean value representing logged-in status
 *
 * @example
 * ```
 * const { username, authenticated, setAuthenticated } = useContext(AuthContext)
 * ```
 */
export const AuthContext = React.createContext<IAuthContext>({
  authenticated: false,
  username: '',
  setAuthenticated: noop,
  setUsername: noop,
});

function usePrevious<T = any>(value: T) {
  const ref = useRef<T>();

  useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref.current;
}

type AuthProviderProps = {
  defaultAuthenticated?: boolean;
  onLogin?: () => void;
  onLogout?: () => void;
  children?: React.ReactNode;
};

/**
 * Provider for AuthContext
 * @remarks
 * Should wrap any & all components where authenticated state is used
 * @example
 * ```javascript
 * <LoginDatabaseProvider>
 *   <AuthProvider>
 *     <Layout>
 *       <AppRoutes />
 *     </Layout>
 *   </AuthProvider>
 * </LoginDatabaseProvider>
 * ```
 */
export const AuthProvider = ({ onLogin, onLogout, children }: AuthProviderProps) => {
  const [authenticated, setAuthenticated] = useSessionStorageEncoded('authToken', 3600000);
  const [username, setUsername] = useState('');

  const { DHA_Key, database } = useContext(LoginDatabaseContext);

  const previousAuthenticated = usePrevious(authenticated);

  // boolean prevents anything wrapped in AuthProvider (probably most of whatever app uses this login module)
  //   from loading before the useSessionStorageEncoded() hook can finish asynchronously setting-up authentication infrastructure.
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // pulls username from LoginDatabase stored in IndexDB, should be the last useEffect to run between AuthProvider.ts and useSessionStorageEncoded.tsx
  // I can't get this useEffect to run when changes to db.userProfile occur. I wish i knew how, but for now i found a hacky workaround
  useEffect(() => {
    const getUsername = async () => {
      if (DHA_Key === undefined) {
        return;
      }

      try {
        const encryptedUser = await database.userProfile.get('primaryUser');
        if (encryptedUser !== undefined) {
          const decryptedUser = JSON.parse(await decrypt(encryptedUser.userProfile, DHA_Key));
          setUsername(decryptedUser.username);
          // renders app
          setIsLoading(false);
        } else {
          setIsLoading(false);
        }
      } catch (err) {
        console.log(err);
      }
    };

    getUsername().catch((err) => console.log(err));
  }, [DHA_Key, database.userProfile]);

  useEffect(() => {
    if (!previousAuthenticated && authenticated) {
      onLogin && onLogin();
    }
  }, [previousAuthenticated, authenticated, onLogin]);

  useEffect(() => {
    if (previousAuthenticated && !authenticated) {
      onLogout && onLogout();
    }
  }, [previousAuthenticated, authenticated, onLogout]);

  const contextValue = useMemo(
    () => ({
      authenticated,
      username,
      setAuthenticated,
      setUsername,
    }),
    [authenticated, setAuthenticated]
  );

  if (isLoading) return <div />;
  else return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};
